import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Select,
  MenuItem,
  TextField,
  Checkbox,
  FormControlLabel,
  IconButton,
  Divider,
  Grid,
  Typography,
  Box,
  Tooltip,
  Alert,
  LinearProgress,
  Snackbar
} from '@mui/material';
import { 
  Close as CloseIcon, 
  InfoOutlined as InfoIcon, 
  HelpOutline as HelpIcon, 
  AutoAwesome as AutoAwesomeIcon, 
  Minimize as MinimizeIcon,
  Settings as SettingsIcon,
  SmartToy as SmartToyIcon,
  RecordVoiceOver as RecordVoiceOverIcon,
  RestaurantMenu as RestaurantMenuIcon,
  CalendarToday as CalendarTodayIcon,
  Event as EventIcon,
  Edit as EditIcon,
  Verified as VerifiedIcon,
  PriorityHigh as PriorityHighIcon,
  Psychology as PsychologyIcon
} from '@mui/icons-material';
import { GenerationOptions, PesoDetectado, RecordatorioDetectado } from '../../types/ai';
import { Email } from '../../types/email';
import aiEmailService, { GenerationProgress, TERMINAL_GENERATION_STATUSES } from '../../services/aiEmailService';
import { v4 as uuidv4 } from 'uuid';
import { getBackendUrl } from '../../config';
import axios from 'axios';
import EmailProgressSSE from './EmailProgressSSE';
import ReactDOM from 'react-dom';
import { prepareBodyForComposition } from './EmailLayout';
import RecordatoriosModal from './RecordatoriosModal';

interface SSEProgressData {
  status: 'starting' | 'in_progress' | 'completed' | 'error' | 'cancelled' | 'waiting' | 'expired';
  message: string;
  percentage?: number;
  event_type?: string;
  elapsed_time?: string;
  phase?: string;
  error?: string;
  result?: string;
  plan_valido?: boolean;
  email?: string;
  peso_detectado?: PesoDetectado;
  recordatorios_detectados?: RecordatorioDetectado[];
}

interface ActiveGenerationRecord {
  generationId: string;
  composeId?: string;
  pacienteId?: string | number;
  selectedMessageId?: string;
  startedAt: number;
  updatedAt: number;
  status: GenerationProgress['status'];
  incluirPlan: boolean;
  hasReviewPending?: boolean;
  pesoReviewed?: boolean;
  recordatoriosReviewed?: boolean;
}

const ACTIVE_GENERATION_STORAGE_PREFIX = 'gpa_active_ai_generation_v1';
const ACTIVE_GENERATION_TTL_MS = 6 * 60 * 60 * 1000;
const WAITING_GENERATION_GRACE_MS = 2 * 60 * 1000;

interface AIEmailGeneratorProps {
  open: boolean;
  onClose: () => void;
  onGenerate?: (options: GenerationOptions) => Promise<void>;
  onGenerationComplete: (result: { success: boolean; email: string }) => void;
  onGenerationCancelled?: () => void;
  onApplyEmail?: (email: string, incluirPlan?: boolean) => void;
  selectedMessage: Email | null;
  emailFilters: string[];
  pacienteData?: any;
  modalPacienteOriginal?: any;
  generationId?: string;
  setGenerationId: React.Dispatch<React.SetStateAction<string | null>>;
  isMinimized?: boolean;
  onMinimize?: () => void;
  onRestore?: () => void;
  composeId?: string;
  onGenerationStateChange?: (state: { isGenerating: boolean; isCompleted: boolean }) => void;
}

const AIEmailGenerator: React.FC<AIEmailGeneratorProps> = ({ 
  open, 
  onClose, 
  onGenerate, 
  onGenerationComplete, 
  onGenerationCancelled, 
  onApplyEmail,
  selectedMessage, 
  emailFilters, 
  pacienteData,
  modalPacienteOriginal,
  generationId,
  setGenerationId,
  isMinimized,
  onMinimize,
  onRestore,
  composeId,
  onGenerationStateChange
}) => {
  // Log pacienteData solo cuando cambie
  useEffect(() => {
    if (pacienteData) {
      // console.log('[DEBUG FRONTEND] AIEmailGenerator recibió pacienteData:', pacienteData);
    }
  }, [pacienteData]);
  
  // Obtener el día actual para establecerlo como predeterminado
  const getDiaActual = () => {
    const diasSemana = ['LUNES', 'MARTES', 'MIÉRCOLES', 'JUEVES', 'VIERNES', 'SÁBADO', 'DOMINGO'];
    const diaActual = new Date().getDay();
    // new Date().getDay() devuelve 0=domingo, 1=lunes, etc.
    // Pero nuestro array empieza con LUNES (índice 0)
    const indiceEnArray = diaActual === 0 ? 6 : diaActual - 1; // Domingo va al final (índice 6)
    return diasSemana[indiceEnArray];
  };

  const [options, setOptions] = useState<GenerationOptions>({
    incluirPlan: true,
    diaInicio: getDiaActual(),
    numDiasObjetivo: 8,
    toneStyle: 'Cercano Informativo',
    model: 'deepseek/deepseek-chat',
    comentarioManual: '',
    editarPlanActivo: false,
    // NUEVO: modelo para la segunda llamada
    modelCall2: '',
    modelJudge: '',
  });
  const [availableModels, setAvailableModels] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  // ELIMINAR: const [showVerificationModelSelector, setShowVerificationModelSelector] = useState(false);
  // NUEVO: estado para modelo de segunda llamada
  const [showCall2ModelSelector, setShowCall2ModelSelector] = useState(false);
  const [isCancelled, setIsCancelled] = useState(false);
  const [progressMessage, setProgressMessage] = useState('');
  const [progressType, setProgressType] = useState<'indeterminate' | 'determinate' | 'completed'>('indeterminate');
  const [progressPercentage, setProgressPercentage] = useState(0);
  const [maxProgressPercentage, setMaxProgressPercentage] = useState(0);
  const [progressPhase, setProgressPhase] = useState('');
  const [maxProgressPhase, setMaxProgressPhase] = useState('');
  const [progressElapsedTime, setProgressElapsedTime] = useState('');
  const [maxProgressElapsedTime, setMaxProgressElapsedTime] = useState('');
  const [closeSSE, setCloseSSE] = useState<(() => void) | null>(null);
  const [modalPeso, setModalPeso] = useState<{ peso: number; fecha: string; mensaje: string } | null>(null);
  const [lastGeneratedEmail, setLastGeneratedEmail] = useState<string>('');
  const [lastSSEData, setLastSSEData] = useState<SSEProgressData | null>(null);
  const [incluirHilo, setIncluirHilo] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [pendingEmailToApply, setPendingEmailToApply] = useState<string | null>(null);
  const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);
  const [internalMinimized, setInternalMinimized] = useState(false);
  const [isGenerationCompleted, setIsGenerationCompleted] = useState(false);
  const [showDiaInicioTooltip, setShowDiaInicioTooltip] = useState(true);
  const [successPending, setSuccessPending] = useState(false);
  const [showRecordatoriosModal, setShowRecordatoriosModal] = useState(false);
  const [recordatoriosDetectados, setRecordatoriosDetectados] = useState<RecordatorioDetectado[]>([]);
  const [reviewFlowStarted, setReviewFlowStarted] = useState(false);

  // ===== NUEVO ESTADO PARA DATOS PENDIENTES DE REVISIÓN =====
  // Almacena los datos detectados (peso/recordatorios) que esperan ser revisados
  // por el usuario. Solo se activan cuando el usuario interactúa con la UI.
  const [pendingReviewData, setPendingReviewData] = useState<{
    peso?: PesoDetectado;
    recordatorios?: RecordatorioDetectado[];
  } | null>(null);
  // ==========================================================

  const modalPesoEstablecido = useRef(false);
  const dummyPesoPromiseRef = useRef<null | (() => void)>(null);
  const planGenerationTimerRef = useRef<NodeJS.Timeout | null>(null);
  const reviewRestoreTriggeredRef = useRef(false);
  const closeSSERef = useRef<(() => void) | null>(null);
  const recoveryInFlightRef = useRef(false);

  // Determinar si está minimizado (prop externa o estado interno)
  const isCurrentlyMinimized = isMinimized !== undefined ? isMinimized : internalMinimized;
  const hasBlockingReviewPending =
    !!modalPeso ||
    showRecordatoriosModal ||
    !!pendingReviewData?.peso ||
    !!(pendingReviewData?.recordatorios && pendingReviewData.recordatorios.length > 0);
  const activeGenerationStorageKey = `${ACTIVE_GENERATION_STORAGE_PREFIX}:${composeId || modalPacienteOriginal?.id || selectedMessage?.id || 'default'}`;

  const readActiveGenerationRecord = (): ActiveGenerationRecord | null => {
    try {
      const raw = window.localStorage.getItem(activeGenerationStorageKey);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as ActiveGenerationRecord;
      if (!parsed?.generationId || !parsed.startedAt) {
        window.localStorage.removeItem(activeGenerationStorageKey);
        return null;
      }
      if (Date.now() - parsed.startedAt > ACTIVE_GENERATION_TTL_MS) {
        window.localStorage.removeItem(activeGenerationStorageKey);
        return null;
      }
      return parsed;
    } catch (error) {
      console.warn('[DEBUG FRONTEND] No se pudo leer generacion activa persistida:', error);
      window.localStorage.removeItem(activeGenerationStorageKey);
      return null;
    }
  };

  const writeActiveGenerationRecord = (record: ActiveGenerationRecord) => {
    try {
      window.localStorage.setItem(activeGenerationStorageKey, JSON.stringify(record));
    } catch (error) {
      console.warn('[DEBUG FRONTEND] No se pudo persistir generacion activa:', error);
    }
  };

  const patchActiveGenerationRecord = (patch: Partial<ActiveGenerationRecord> & { generationId?: string }) => {
    const current = readActiveGenerationRecord();
    const generationIdToStore = patch.generationId || current?.generationId;
    if (!generationIdToStore) return;
    writeActiveGenerationRecord({
      generationId: generationIdToStore,
      composeId,
      pacienteId: modalPacienteOriginal?.id || pacienteData?.id,
      selectedMessageId: selectedMessage?.id,
      startedAt: current?.startedAt || Date.now(),
      updatedAt: Date.now(),
      status: patch.status || current?.status || 'starting',
      incluirPlan: patch.incluirPlan ?? current?.incluirPlan ?? options.incluirPlan,
      hasReviewPending: patch.hasReviewPending ?? current?.hasReviewPending,
      pesoReviewed: patch.pesoReviewed ?? current?.pesoReviewed,
      recordatoriosReviewed: patch.recordatoriosReviewed ?? current?.recordatoriosReviewed,
    });
  };

  const clearActiveGenerationRecord = () => {
    try {
      window.localStorage.removeItem(activeGenerationStorageKey);
    } catch (error) {
      console.warn('[DEBUG FRONTEND] No se pudo limpiar generacion activa:', error);
    }
  };

  const stopProgressPolling = () => {
    if (closeSSERef.current) {
      closeSSERef.current();
      closeSSERef.current = null;
    }
    if (closeSSE) {
      closeSSE();
    }
    setCloseSSE(null);
  };

  // Notificar cambios de estado de generación al componente padre
  useEffect(() => {
    if (onGenerationStateChange) {
      onGenerationStateChange({
        isGenerating: isLoading,
        isCompleted: isGenerationCompleted
      });
    }
  }, [isLoading, isGenerationCompleted]);

  // Handler para minimizar
  const handleMinimize = () => {
    if (hasBlockingReviewPending) {
      console.log('[DEBUG FRONTEND] Minimización bloqueada: hay revisión pendiente de peso/recordatorios.');
      return;
    }
    if (onMinimize) {
      onMinimize();
    } else {
      setInternalMinimized(true);
    }
  };

  // Handler para restaurar
  const handleRestore = () => {
    if (onRestore) {
      onRestore();
    } else {
      setInternalMinimized(false);
    }
  };

  // ===== FUNCIÓN NUEVA PARA INICIAR EL FLUJO DE REVISIÓN =====
  // Esta función se ejecuta cuando el usuario interactúa con la UI después de que
  // la generación ha terminado. Inicia el flujo de modales (peso/recordatorios)
  // en el orden correcto, solo cuando el usuario está activamente viendo la pantalla.
  const startReviewFlow = () => {
    // Usamos una función para obtener el estado más reciente, para evitar problemas de "stale state"
    setPendingReviewData(currentPendingData => {
      if (!currentPendingData) return null;

      setReviewFlowStarted(true);

      const { peso, recordatorios } = currentPendingData;

      if (peso) {
        console.log('[DEBUG FRONTEND] Iniciando flujo de revisión con modal de peso.');
        setModalPeso({
          peso: peso.peso,
          fecha: peso.fecha,
          mensaje: peso.mensaje,
        });
      } else if (recordatorios && recordatorios.length > 0) {
        console.log('[DEBUG FRONTEND] Iniciando flujo de revisión con modal de recordatorios.');
        setRecordatoriosDetectados(recordatorios);
        setShowRecordatoriosModal(true);
      }
      
      // Limpiamos los datos pendientes una vez que el flujo ha comenzado
      return null;
    });
  };
  // ==============================================================

  useEffect(() => {
    if (open && availableModels.length === 0) {
      aiEmailService.getAvailableModels().then(models => {
        setAvailableModels(models);
        if (models.length > 0) {
          // Siempre usar el primer modelo de la lista como modelo principal
          setOptions(prev => ({ 
            ...prev, 
            model: models[0]
          }));
        }
      });
    }
  }, [open]);

  // Efecto para manejar modelo de segunda llamada - asignar modelo por defecto
  useEffect(() => {
    if (!options.modelCall2 && availableModels.length > 0) {
      // Usar el segundo modelo disponible como modelo de segunda llamada por defecto, o el primero si solo hay uno
      const defaultCall2Model = availableModels.length > 1 ? availableModels[1] : availableModels[0];
      setOptions(prev => ({ ...prev, modelCall2: defaultCall2Model }));
    }
  }, [options.modelCall2, availableModels]);

  // Modelo barato para jueces: no decide estrategia ni genera planes.
  useEffect(() => {
    if (!options.modelJudge && availableModels.length > 0) {
      const cheapJudgeModel =
        availableModels.find(model => model === 'google/gemini-2.5-flash-lite') ||
        availableModels.find(model => model === 'openai/gpt-4.1-nano');
      const preferredJudgeModel = cheapJudgeModel || options.modelCall2;
      if (!preferredJudgeModel) return;
      setOptions(prev => ({ ...prev, modelJudge: preferredJudgeModel }));
    }
  }, [options.modelJudge, options.modelCall2, availableModels]);

  // Timer para añadir progreso visual después de 20 segundos en "Generando plan alimentario..."
  useEffect(() => {
    // Limpiar timer anterior si existe
    if (planGenerationTimerRef.current) {
      clearTimeout(planGenerationTimerRef.current);
      planGenerationTimerRef.current = null;
    }

    // Si el mensaje es "Generando plan alimentario..." y está cargando, iniciar timer
    if (progressMessage.includes('Generando plan alimentario') && isLoading) {
      planGenerationTimerRef.current = setTimeout(() => {
        // Añadir 30% más al progreso visual (sin afectar la lógica real)
        setMaxProgressPercentage(prev => {
          const newProgress = Math.min(prev + 30, 95); // No superar 95% para no interferir con el 100% real
          return newProgress;
        });
      }, 25000); // 25 segundos
    }

    // Cleanup al desmontar o cambiar mensaje
    return () => {
      if (planGenerationTimerRef.current) {
        clearTimeout(planGenerationTimerRef.current);
        planGenerationTimerRef.current = null;
      }
    };
  }, [progressMessage, isLoading]);

  const handleGenerationProgress = (progress: GenerationProgress) => {
    setLastSSEData(progress);
    const newMessage = (progress.message || '').replace(/\s*\(\d+%?\s*-\s*[\d.]+s\)\s*$/, '');
    const isWaitingMessage = newMessage.toLowerCase().includes('esperando inicio');

    setProgressMessage(prevMsg => {
      if (isWaitingMessage && prevMsg && !prevMsg.toLowerCase().includes('esperando inicio')) {
        return prevMsg;
      }
      return newMessage || prevMsg || 'Actualizando progreso...';
    });

    const newPercentage = progress.percentage || 0;
    setProgressPercentage(newPercentage);
    setMaxProgressPercentage(prev => Math.max(prev, newPercentage));

    const newPhase = progress.phase || progress.event_type || '';
    setProgressPhase(newPhase);
    if (newPhase && !newPhase.toLowerCase().includes('esperando inicio')) {
      setMaxProgressPhase(prev => (!prev || newPhase !== prev ? newPhase : prev));
    }

    const newElapsedTime = progress.elapsed_time || '';
    setProgressElapsedTime(newElapsedTime);
    if (newElapsedTime) {
      setMaxProgressElapsedTime(prev => (!prev || newElapsedTime !== prev ? newElapsedTime : prev));
    }

    if (progress.percentage !== undefined) {
      setProgressType('determinate');
    }

    if (progress.status === 'completed') {
      if (planGenerationTimerRef.current) {
        clearTimeout(planGenerationTimerRef.current);
        planGenerationTimerRef.current = null;
      }

      const peso = progress.peso_detectado;
      const recordatorios = progress.recordatorios_detectados;
      const hasReviewPending = !!peso || !!(recordatorios && recordatorios.length > 0);
      patchActiveGenerationRecord({
        generationId: progress.generation_id || generationId,
        status: 'completed',
        hasReviewPending
      });

      setProgressMessage('Generacion completada exitosamente');
      setProgressPercentage(100);
      setProgressType('completed');
      setIsLoading(false);
      setIsGenerationCompleted(true);
      setLastGeneratedEmail(progress.email || progress.result || 'Email generado exitosamente');
    } else if (progress.status === 'error' || progress.status === 'cancelled' || progress.status === 'expired') {
      if (planGenerationTimerRef.current) {
        clearTimeout(planGenerationTimerRef.current);
        planGenerationTimerRef.current = null;
      }

      patchActiveGenerationRecord({
        generationId: progress.generation_id || generationId,
        status: progress.status,
        hasReviewPending: false
      });
      setProgressMessage(progress.error || progress.message || 'La generacion se detuvo');
      setProgressPercentage(progress.percentage ?? 100);
      setProgressType('completed');
      setIsLoading(false);
      setIsGenerationCompleted(false);
      clearActiveGenerationRecord();
    } else {
      patchActiveGenerationRecord({
        generationId: progress.generation_id || generationId,
        status: progress.status,
        hasReviewPending: false
      });
      setIsLoading(true);
      setIsGenerationCompleted(false);
    }
  };

  const startProgressPolling = (genId: string) => {
    stopProgressPolling();
    const closeProgressPolling = aiEmailService.listenToProgress(
      genId,
      handleGenerationProgress,
      (error) => {
        console.warn('[DEBUG FRONTEND] Error transitorio en polling de generacion:', error);
        setProgressMessage(prev => prev || 'Reconectando con el progreso de generacion...');
      },
      () => {
        closeSSERef.current = null;
        setCloseSSE(null);
      }
    );
    closeSSERef.current = closeProgressPolling;
    setCloseSSE(() => closeProgressPolling);
  };

  const recoverActiveGeneration = async (reason: string) => {
    const record = readActiveGenerationRecord();
    if (!record || recoveryInFlightRef.current) return;

    recoveryInFlightRef.current = true;
    try {
      console.log('[DEBUG FRONTEND] Recuperando generacion activa:', { reason, record });
      setGenerationId(record.generationId);
      setOptions(prev => ({ ...prev, incluirPlan: record.incluirPlan }));

      const progress = await aiEmailService.getGenerationProgress(record.generationId);
      if (
        progress.status === 'waiting' &&
        Date.now() - record.startedAt > WAITING_GENERATION_GRACE_MS
      ) {
        handleGenerationProgress({
          status: 'expired',
          message: 'No se encontro progreso activo para esta generacion. Podes reintentar sin que quede colgado.',
          percentage: 100,
          generation_id: record.generationId
        });
        return;
      }

      handleGenerationProgress(progress);
      if (!TERMINAL_GENERATION_STATUSES.has(progress.status)) {
        startProgressPolling(record.generationId);
      } else if (progress.status === 'completed' && onRestore && isCurrentlyMinimized) {
        onRestore();
      }
    } catch (error) {
      console.warn('[DEBUG FRONTEND] No se pudo recuperar estado puntual; reenganchando polling:', error);
      setGenerationId(record.generationId);
      setIsLoading(true);
      setProgressMessage('Reconectando con la generacion...');
      startProgressPolling(record.generationId);
    } finally {
      recoveryInFlightRef.current = false;
    }
  };

  useEffect(() => {
    recoverActiveGeneration('mount');

    const handleResume = () => {
      if (!document.hidden) {
        recoverActiveGeneration('resume');
      }
    };

    document.addEventListener('visibilitychange', handleResume);
    window.addEventListener('focus', handleResume);
    window.addEventListener('pageshow', handleResume);
    window.addEventListener('online', handleResume);

    return () => {
      document.removeEventListener('visibilitychange', handleResume);
      window.removeEventListener('focus', handleResume);
      window.removeEventListener('pageshow', handleResume);
      window.removeEventListener('online', handleResume);
    };
  }, [activeGenerationStorageKey]);

  const handleInternalGenerate = async () => {
    // Limpiar timer de plan alimentario si existe
    if (planGenerationTimerRef.current) {
      clearTimeout(planGenerationTimerRef.current);
      planGenerationTimerRef.current = null;
    }

    setIsLoading(true);
    setIsCancelled(false);
    setIsGenerationCompleted(false);
    setReviewFlowStarted(false);
    reviewRestoreTriggeredRef.current = false;
    stopProgressPolling();
    clearActiveGenerationRecord();
    setGenerationId(null);
    setProgressMessage('Iniciando generación...');
    setProgressType('indeterminate');
    setProgressPercentage(0);
    setMaxProgressPercentage(0);
    setProgressPhase('');
    setMaxProgressPhase('');
    setProgressElapsedTime('');
    setMaxProgressElapsedTime('');
    
    const genId = uuidv4();
    setGenerationId(genId);
    
    // Log para verificar qué datos del paciente se envían
    const pacienteDataToSend = modalPacienteOriginal || pacienteData;
    writeActiveGenerationRecord({
      generationId: genId,
      composeId,
      pacienteId: pacienteDataToSend?.id,
      selectedMessageId: selectedMessage?.id,
      startedAt: Date.now(),
      updatedAt: Date.now(),
      status: 'starting',
      incluirPlan: options.incluirPlan,
      hasReviewPending: false,
      pesoReviewed: false,
      recordatoriosReviewed: false
    });
    // console.log('[DEBUG FRONTEND] Datos del paciente que se envían al backend:', {
    //   modalPacienteOriginal: modalPacienteOriginal ? {
    //     id: modalPacienteOriginal.id,
    //     nombre: modalPacienteOriginal.nombre,
    //     email: modalPacienteOriginal.email,
    //     email2: modalPacienteOriginal.email2,
    //     edad: modalPacienteOriginal.edad,
    //     genero: modalPacienteOriginal.genero,
    //     religion: modalPacienteOriginal.religion,
    //     preferencias: modalPacienteOriginal.preferencias,
    //     intolerancias: modalPacienteOriginal.intolerancias,
    //     observaciones_alimentacion: modalPacienteOriginal.observaciones_alimentacion,
    //     analisis_resumen: modalPacienteOriginal.analisis_resumen,
    //     medicamentos: modalPacienteOriginal.medicamentos,
    //     pesos: modalPacienteOriginal.pesos
    //   } : null,
    //   pacienteData: pacienteData ? {
    //     id: pacienteData.id,
    //     nombre: pacienteData.nombre,
    //     email: pacienteData.email,
    //     email2: pacienteData.email2
    //   } : null,
    //   pacienteDataToSend: pacienteDataToSend ? {
    //     id: pacienteDataToSend.id,
    //     nombre: pacienteDataToSend.nombre,
    //     email: pacienteDataToSend.email,
    //     email2: pacienteDataToSend.email2
    //   } : null
    // });
    
    try {
      // Iniciar el polling de progreso ANTES de esperar la respuesta del POST
      startProgressPolling(genId);
      /*
      const closeProgressPolling = aiEmailService.listenToProgress(
        genId,
        (progress: GenerationProgress) => {
          setLastSSEData(progress);
          const newMessage = progress.message.replace(/\s*\(\d+%?\s*-\s*[\d.]+s\)\s*$/, '');
          setProgressMessage(prevMsg => {
            if (
              newMessage === 'Esperando inicio de generación...' &&
              prevMsg && prevMsg !== 'Esperando inicio de generación...'
            ) {
              // No sobrescribir si ya hay un mensaje más avanzado
              return prevMsg;
            }
            return newMessage;
          });
          
          // Mantener el porcentaje más alto alcanzado
          const newPercentage = progress.percentage || 0;
          setProgressPercentage(newPercentage);
          setMaxProgressPercentage(prev => Math.max(prev, newPercentage));
          
          // Mantener la fase más avanzada
          const newPhase = progress.phase || progress.event_type || '';
          setProgressPhase(newPhase);
          if (newPhase && newPhase !== 'Esperando inicio de generación...') {
            setMaxProgressPhase(prev => {
              // Solo actualizar si la nueva fase es más avanzada o diferente
              if (!prev || newPhase !== prev) {
                return newPhase;
              }
              return prev;
            });
          }
          
          // Mantener el tiempo más alto alcanzado
          const newElapsedTime = progress.elapsed_time || '';
          setProgressElapsedTime(newElapsedTime);
          if (newElapsedTime) {
            setMaxProgressElapsedTime(prev => {
              // Solo actualizar si el nuevo tiempo es mayor o si no hay tiempo previo
              if (!prev || newElapsedTime !== prev) {
                return newElapsedTime;
              }
              return prev;
            });
          }
          if (progress.percentage !== undefined) {
            setProgressType('determinate');
          }
          if (progress.status === 'completed') {
            // Limpiar timer de plan alimentario si existe
            if (planGenerationTimerRef.current) {
              clearTimeout(planGenerationTimerRef.current);
              planGenerationTimerRef.current = null;
            }

            setProgressMessage('Generación completada exitosamente');
            setProgressPercentage(100);
            setProgressType('completed');
            setIsLoading(false);
            setIsGenerationCompleted(true);
            if (progress.email) {
              setLastGeneratedEmail(progress.email);
            } else if (progress.result) {
              setLastGeneratedEmail(progress.result);
            } else {
              setLastGeneratedEmail('Email generado exitosamente');
            }
          } else if (progress.status === 'error' || progress.status === 'cancelled') {
            if (planGenerationTimerRef.current) {
              clearTimeout(planGenerationTimerRef.current);
              planGenerationTimerRef.current = null;
            }
            setProgressMessage(progress.error || progress.message || 'La generacion se detuvo');
            setProgressPercentage(progress.percentage ?? 100);
            setProgressType('completed');
            setIsLoading(false);
            setIsGenerationCompleted(false);
          }
        },
        (error) => {
          console.warn('[DEBUG FRONTEND] Error transitorio en polling de generación:', error);
          setProgressMessage(prev => prev || 'Reconectando con el progreso de generación...');
        },
        () => {
          setCloseSSE(null);
        }
      );
      setCloseSSE(() => closeProgressPolling);
      */

      // Iniciar la generación en el backend
      await aiEmailService.generateEmail(
        options,
        selectedMessage?.id || null,
        emailFilters,
        pacienteDataToSend,
        genId,
        selectedMessage?.receivedDateTime || undefined
      );
      
      setTimeout(() => {
        const modal = document.querySelector('.MuiDialogContent-root');
        if (modal) modal.scrollTop = 0;
      }, 0);
      
    } catch (error) {
      // Limpiar timer de plan alimentario si existe
      if (planGenerationTimerRef.current) {
        clearTimeout(planGenerationTimerRef.current);
        planGenerationTimerRef.current = null;
      }

      console.error('[DEBUG FRONTEND] Error al iniciar generación:', error);
      setProgressMessage('Error al iniciar la generación');
      setProgressType('completed');
      setIsLoading(false);
      stopProgressPolling();
      clearActiveGenerationRecord();
    }
  };

  const handleCancel = async () => {
    // Limpiar timer de plan alimentario si existe
    if (planGenerationTimerRef.current) {
      clearTimeout(planGenerationTimerRef.current);
      planGenerationTimerRef.current = null;
    }

    // Solo mostrar modal de confirmación si hay una generación en curso
    if (hasBlockingReviewPending) {
      console.log('[DEBUG FRONTEND] Cierre bloqueado: hay revision pendiente de peso/recordatorios.');
      if (onRestore) onRestore();
      return;
    }

    if (isLoading) {
      setShowCancelConfirmation(true);
    } else {
      // Si no hay generación en curso, cerrar directamente
      onClose();
    }
  };

  const handleConfirmCancel = async () => {
    // Limpiar timer de plan alimentario si existe
    if (planGenerationTimerRef.current) {
      clearTimeout(planGenerationTimerRef.current);
      planGenerationTimerRef.current = null;
    }

    setIsCancelled(true);
    setProgressMessage('Cancelando generación...');
    
    if (generationId) {
      try {
        // Cancelar mediante el endpoint POST dedicado
        await fetch(`${getBackendUrl()}/cancel-generation`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ generation_id: generationId })
        });
      } catch (error) {
        console.error('Error al cancelar generación:', error);
      }
    }
    
    // Cerrar conexión SSE
    stopProgressPolling();
    
    // RESETEAR ESTADO DEL MODAL
    setIsLoading(false);
    setIsCancelled(false);
    setIsGenerationCompleted(false);
    setReviewFlowStarted(false);
    reviewRestoreTriggeredRef.current = false;
    setProgressMessage('');
    setProgressType('indeterminate');
    setProgressPercentage(0);
    setMaxProgressPercentage(0);
    setProgressPhase('');
    setMaxProgressPhase('');
    setProgressElapsedTime('');
    setMaxProgressElapsedTime('');
    setLastSSEData(null);
    setLastGeneratedEmail('');
    setModalPeso(null);
    setShowSuccessModal(false);
    setPendingEmailToApply(null);
    modalPesoEstablecido.current = false;
    setGenerationId(null);
    clearActiveGenerationRecord();
    
    // Cerrar modal de confirmación
    setShowCancelConfirmation(false);
    
    // Notificar al componente padre que se canceló la generación
    // Esto cerrará completamente el modal de IA
    if (onGenerationCancelled) {
      // console.log('[DEBUG FRONTEND] Llamando a onGenerationCancelled para cerrar el modal');
      onGenerationCancelled();
    }
    
    // NO llamar a onClose() aquí, ya que handleGenerationCancelled se encarga de cerrar el modal
  };

  const handleCancelConfirmation = () => {
    // Solo cerrar el modal de confirmación, no cancelar la generación
    setShowCancelConfirmation(false);
  };
  
  const diasSemana = ['LUNES', 'MARTES', 'MIÉRCOLES', 'JUEVES', 'VIERNES', 'SÁBADO', 'DOMINGO'];
  const tonos = [
    'Cercano Informativo',
    'Educativo',
    'Casual'
  ];

  // ===== FUNCIÓN CORREGIDA PARA MANEJAR EL FLUJO DE PESO =====
  // CORRECCIÓN CRÍTICA: Esta función ahora consulta lastSSEData directamente para los recordatorios,
  // porque pendingReviewData ya fue limpiado por startReviewFlow() cuando se mostró el modal de peso.
  const handleConfirmarPeso = async (acepta: boolean) => {
    console.log('[DEBUG FRONTEND] handleConfirmarPeso llamado con:', { acepta, modalPeso, modalPacienteOriginal });
    
    if (modalPeso && acepta && modalPacienteOriginal && modalPacienteOriginal.id) {
      try {
        console.log('[DEBUG FRONTEND] Intentando guardar peso:', {
          pacienteId: modalPacienteOriginal.id,
          peso: modalPeso.peso,
          fecha: modalPeso.fecha,
          url: `${getBackendUrl()}/pacientes/${modalPacienteOriginal.id}/guardar-peso`
        });
        
        const response = await axios.post(`${getBackendUrl()}/pacientes/${modalPacienteOriginal.id}/guardar-peso`, {
          peso: modalPeso.peso,
          fecha: modalPeso.fecha,
        });
        
        console.log('[DEBUG FRONTEND] Peso guardado exitosamente:', response.data);
      } catch (error) {
        console.error('[DEBUG FRONTEND] Error al guardar el peso:', error);
        if (axios.isAxiosError(error)) {
          console.error('[DEBUG FRONTEND] Error de Axios:', {
            status: error.response?.status,
            data: error.response?.data,
            message: error.message
          });
        }
      }
    } else {
      console.log('[DEBUG FRONTEND] No se puede guardar peso:', {
        modalPeso: !!modalPeso,
        acepta,
        modalPacienteOriginal: !!modalPacienteOriginal,
        pacienteId: modalPacienteOriginal?.id
      });
    }
    
    // Cerrar modal de peso (inicia primer render)
    console.log('[DEBUG FRONTEND] Cerrando modal de peso (setModalPeso(null))');
    setModalPeso(null);
    patchActiveGenerationRecord({
      pesoReviewed: true,
      hasReviewPending: !!(lastSSEData?.recordatorios_detectados?.length)
    });
    
    // Forzar segundo render con setTimeout
    setTimeout(() => {
      console.log('[DEBUG FRONTEND] (setTimeout) Ejecutando lógica post-cierre de peso');
      // ===== INICIO DE LA CORRECCIÓN CLAVE =====
      // Revisa 'lastSSEData' para los recordatorios, porque 'pendingReviewData' ya fue limpiado.
      const recordatorios = lastSSEData?.recordatorios_detectados;
      console.log('[DEBUG FRONTEND] (setTimeout) Verificando recordatorios:', {
        recordatorios,
        cantidad: recordatorios?.length,
        lastSSEData: lastSSEData
      });
      
      if (recordatorios && recordatorios.length > 0) {
        console.log('[DEBUG FRONTEND] (setTimeout) Mostrando modal de recordatorios con:', recordatorios);
        setRecordatoriosDetectados(recordatorios);
        setShowRecordatoriosModal(true);
      } else {
        console.log('[DEBUG FRONTEND] (setTimeout) No hay recordatorios, terminando flujo');
        setSuccessPending(true);
      }
      // ===== FIN DE LA CORRECCIÓN CLAVE =====
    }, 100); // Aumentado a 100ms para dar más tiempo
  };
  // ==============================================================

  // =====================================================================================
  // ===== INICIO DE LA LÓGICA MODIFICADA PARA MANEJAR LA FINALIZACIÓN DE GENERACIÓN =====
  // =====================================================================================
  // 
  // FLUJO CORREGIDO:
  // 1. Backend termina generación → lastSSEData.status = 'completed'
  // 2. Frontend prepara datos → setPendingReviewData() (NO muestra modales)
  // 3. Robotito se pone verde → setIsGenerationCompleted(true)
  // 4. Usuario hace clic en robotito → Modal se abre/restaura
  // 5. useEffect se dispara → Detecta !isCurrentlyMinimized && isGenerationCompleted
  // 6. Flujo de revisión inicia → startReviewFlow() o setSuccessPending(true)
  // 7. Modales se muestran → Solo cuando usuario está activamente viendo la pantalla
  // 8. Flujo se completa → Snackbar de éxito y aplicación del email
  //
  // 1. ESTE useEffect AHORA SÓLO PREPARA LOS DATOS, NO MUESTRA NADA
  //    - Cuando la generación termina, solo guarda los datos detectados (peso/recordatorios)
  //    - NO muestra modales automáticamente
  //    - Espera a que el usuario interactúe con la UI
  useEffect(() => {
    if (lastSSEData?.status === 'completed' && lastSSEData.email) {
      setLastGeneratedEmail(lastSSEData.email);
      setIsGenerationCompleted(true);
      if (onGenerationStateChange) onGenerationStateChange({ isGenerating: false, isCompleted: true });

      const activeRecord = readActiveGenerationRecord();
      const peso = activeRecord?.pesoReviewed ? undefined : lastSSEData.peso_detectado;
      const recordatorios = activeRecord?.recordatoriosReviewed ? [] : lastSSEData.recordatorios_detectados;

      console.log('[DEBUG FRONTEND] Generación completada. Preparando datos para revisión:', {
        peso: !!peso,
        recordatorios: recordatorios?.length || 0,
      });

      // Si hay algo que revisar (peso o recordatorios), lo guardamos en el estado pendiente.
      if (peso || (recordatorios && recordatorios.length > 0)) {
        setPendingReviewData({ peso, recordatorios });
      } else {
        // Si no hay nada que revisar, el flujo de éxito se activará cuando el usuario
        // restaure el modal (vea el paso 2).
      }
    }
  }, [lastSSEData]);

  useEffect(() => {
    const hasPendingReview = !!pendingReviewData?.peso || !!(pendingReviewData?.recordatorios && pendingReviewData.recordatorios.length > 0);
    if (
      open &&
      isCurrentlyMinimized &&
      isGenerationCompleted &&
      hasPendingReview &&
      !reviewFlowStarted &&
      onRestore &&
      !reviewRestoreTriggeredRef.current
    ) {
      reviewRestoreTriggeredRef.current = true;
      console.log('[DEBUG FRONTEND] Restaurando modal de IA automáticamente para revisión de peso/recordatorios.');
      onRestore();
    }
  }, [open, isCurrentlyMinimized, isGenerationCompleted, pendingReviewData, reviewFlowStarted, onRestore]);

  useEffect(() => {
    if (open && isCurrentlyMinimized && hasBlockingReviewPending && onRestore) {
      console.log('[DEBUG FRONTEND] Restaurando automáticamente el modal porque la revisión sigue pendiente.');
      onRestore();
    }
  }, [open, isCurrentlyMinimized, hasBlockingReviewPending, onRestore]);

  // 2. ESTE NUEVO useEffect ES EL ÚNICO DISPARADOR DEL FLUJO DE POST-GENERACIÓN
  //    - Se activa cuando el modal se abre/restaura Y la generación ya se ha completado
  //    - Si hay datos pendientes (peso/recordatorios), inicia el flujo de modales
  //    - Si no hay datos pendientes, activa directamente el snackbar de éxito
  //    - MEJORA: pendingReviewData agregado a las dependencias para evitar race conditions
  useEffect(() => {
    // Se activa solo cuando el modal se abre/restaura Y la generación ya se ha completado.
    if (!isCurrentlyMinimized && isGenerationCompleted) {
      if (pendingReviewData) {
        // Si hay datos pendientes de revisión, iniciamos el flujo de modales.
        startReviewFlow();
      } else if (!reviewFlowStarted && !modalPeso && !showRecordatoriosModal) {
        // Guardia contra race condition: pendingReviewData puede estar null porque el
        // useEffect([lastSSEData]) aún no corrió. Verificar directamente en lastSSEData
        // si hay peso o recordatorios pendientes antes de activar el snackbar de éxito.
        const activeRecord = readActiveGenerationRecord();
        const hasPendingInSSE = (!!(lastSSEData?.peso_detectado) && !activeRecord?.pesoReviewed) ||
                                 (!!(lastSSEData?.recordatorios_detectados?.length) && !activeRecord?.recordatoriosReviewed);
        if (!hasPendingInSSE) {
          // Solo si no hay nada en lastSSEData tampoco, la generación no tiene nada que revisar.
          setSuccessPending(true);
        }
        // Si hasPendingInSSE=true pero pendingReviewData=null: race condition.
        // useEffect([lastSSEData]) seteará pendingReviewData en el próximo render
        // y este useEffect se disparará de nuevo con pendingReviewData ya correcto.
      }
    }
  }, [isCurrentlyMinimized, isGenerationCompleted, pendingReviewData, reviewFlowStarted, modalPeso, showRecordatoriosModal, lastSSEData]);

  // 3. ESTE useEffect se mantiene igual: Muestra el snackbar de éxito cuando todo ha terminado.
  //    - Se activa cuando successPending es true y el modal no está minimizado
  //    - Muestra el snackbar de éxito y aplica el email generado
  useEffect(() => {
    if (!isCurrentlyMinimized && successPending) {
      setSuccessPending(false); // Resetear
      // Llama a la nueva función que NO cierra el modal
      showSuccessSnackbar({ success: true, email: lastGeneratedEmail || '' });
    }
  }, [isCurrentlyMinimized, successPending, lastGeneratedEmail]);

  // NOTA: Este useEffect duplicado se eliminó para evitar conflictos.
  // El useEffect anterior (línea ~650) ya maneja correctamente el flujo de éxito.
  
  // ===================================================================================
  // ===== FIN DE LA LÓGICA MODIFICADA =================================================
  // ===================================================================================

  const handleSuccessSnackbarClose = () => {
    setShowSuccessModal(false);
    if (pendingEmailToApply && onApplyEmail) {
      onApplyEmail(pendingEmailToApply, options.incluirPlan);
    }
    // También llamar a onGenerationComplete para mantener compatibilidad
    if (pendingEmailToApply) {
      onGenerationComplete({ success: true, email: pendingEmailToApply });
    }
    setPendingEmailToApply(null);
    setLastGeneratedEmail('');
    reviewRestoreTriggeredRef.current = false;
    
    stopProgressPolling();
    clearActiveGenerationRecord();
    setGenerationId(null);
    onClose(); // <-- AÑADE ESTA LÍNEA AQUÍ. El modal se cierra al final de todo.
  };

  // Evitar que el flujo vuelva a disparar el Snackbar si ya se mostró
  useEffect(() => {
    if (showSuccessModal && pendingEmailToApply === null) {
      setShowSuccessModal(false);
    }
  }, [showSuccessModal, pendingEmailToApply]);

  const showSuccessSnackbar = (result: { success: boolean; email: string }) => {
    try {
      if (modalPeso || showRecordatoriosModal) {
        console.log('[DEBUG FRONTEND] Hay un modal de revisión abierto, no mostrando snackbar aún.');
        return;
      }
      let finalEmail = result.email;
      if (incluirHilo && selectedMessage) {
        // Usar la misma lógica de onCompose para armar el hilo
        const hilo = prepareBodyForComposition(selectedMessage, 'reply');
        finalEmail = `${result.email}${hilo}`;
      }
      setLastGeneratedEmail(finalEmail);
      setPendingEmailToApply(finalEmail); // Guardar el email pendiente
      setShowSuccessModal(true); // Solo muestra el snackbar, no cierra nada
      // onClose(); // <-- ESTA LÍNEA SE ELIMINA. ¡ESTE ES EL CAMBIO MÁS IMPORTANTE!
    } catch (error) {
      console.error('Error al procesar el email generado:', error);
    }
  };

  useEffect(() => {
    // Si estamos en modo dummy y el modal de peso se cierra, resolvemos la promesa
    if (!modalPeso && dummyPesoPromiseRef.current) {
      dummyPesoPromiseRef.current();
      dummyPesoPromiseRef.current = null;
    }
  }, [modalPeso]);

  const handleDummyGenerate = async () => {
    setIsLoading(true);
    if (onGenerationStateChange) onGenerationStateChange({ isGenerating: true, isCompleted: false });
    setIsCancelled(false);
    setIsGenerationCompleted(false);
    setProgressMessage('Iniciando generación...');
    setProgressType('indeterminate');
    setProgressPercentage(0);
    setMaxProgressPercentage(0);
    setProgressPhase('');
    setMaxProgressPhase('');
    setProgressElapsedTime('');
    setMaxProgressElapsedTime('');

    // Simular progreso
    await new Promise(res => setTimeout(res, 700));
    setProgressMessage('Analizando contexto...');
    setProgressPercentage(20);
    setProgressType('determinate');
    await new Promise(res => setTimeout(res, 700));
    setProgressMessage('Generando plan alimentario...');
    setProgressPercentage(60);
    setProgressType('determinate');
    await new Promise(res => setTimeout(res, 900));
    setProgressMessage('Finalizando...');
    setProgressPercentage(90);
    setProgressType('determinate');
    await new Promise(res => setTimeout(res, 600));

    // Al terminar la generación, poner los robots en tick inmediatamente
    setIsLoading(false);
    setIsGenerationCompleted(true);
    if (onGenerationStateChange) onGenerationStateChange({ isGenerating: false, isCompleted: true });

    let pesoSimulado = true;
    if (pesoSimulado) {
      setModalPeso({ peso: 72.3, fecha: '2024-06-10', mensaje: 'Se detectó un nuevo peso en la respuesta.' });
      // Esperar a que el usuario cierre el modal de peso
      await new Promise<void>(resolve => {
        dummyPesoPromiseRef.current = resolve;
      });
    }

    // Simular email generado
    setProgressMessage('Generación completada exitosamente');
    setProgressPercentage(100);
    setProgressType('completed');
    setLastGeneratedEmail('Este es un email generado de prueba por el modo dummy.\n\nSaludos!');

    // SIEMPRE dejar el éxito pendiente, nunca mostrar/cerrar aquí
    setSuccessPending(true);
  };

  const handleRecordatoriosGuardados = () => {
    console.log('[DEBUG FRONTEND] Recordatorios guardados, cerrando modal');
    setShowRecordatoriosModal(false);
    setRecordatoriosDetectados([]);
    patchActiveGenerationRecord({ recordatoriosReviewed: true, hasReviewPending: false });
    // Después de guardar, hemos terminado el flujo.
    setSuccessPending(true);
  };

  // Función para cuando se cierra el modal de recordatorios sin guardar
  const handleCloseRecordatoriosModal = () => {
    console.log('[DEBUG FRONTEND] Modal de recordatorios cerrado sin guardar');
    setShowRecordatoriosModal(false);
    setRecordatoriosDetectados([]);
    patchActiveGenerationRecord({ recordatoriosReviewed: true, hasReviewPending: false });
    // Al cerrar sin guardar, también hemos terminado el flujo.
    setSuccessPending(true);
  };

  return (
    <>
      {/* Backdrop sutil cuando está minimizado */}
      {open && isCurrentlyMinimized && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            bgcolor: 'transparent', // Completamente transparente
            zIndex: 1000,
            pointerEvents: 'none', // No bloquear interacciones
          }}
        />
      )}
      
      <Dialog 
        open={open && !isCurrentlyMinimized}
        onClose={(event, reason) => {
          if (hasBlockingReviewPending) {
            console.log('[DEBUG FRONTEND] Cierre bloqueado: hay revisión pendiente de peso/recordatorios.');
            return;
          }
          // Si está generando, minimizar en lugar de cerrar
          if (isLoading && reason === 'backdropClick') {
            handleMinimize();
          } else {
            onClose();
          }
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: '12px' } }}
        sx={{ zIndex: 2000 }}
        BackdropProps={{
          sx: {
            backgroundColor: 'rgba(0,0,0,0.5)',
            // zIndex: 1999 // COMENTADO: estaba tapando el modal
          }
        }}
      >
        <style>
          {`
            @keyframes loading {
              0% { background-position: 200% 0; }
              100% { background-position: -200% 0; }
            }
            
            @keyframes shimmer {
              0% { transform: translateX(-100%); }
              100% { transform: translateX(100%); }
            }
            
            .progress-bar-container {
              position: relative;
              width: 100%;
              height: 8px;
              background-color: rgba(0, 0, 0, 0.1);
              border-radius: 4px;
              overflow: hidden;
            }
            
            .progress-bar-fill {
              height: 100%;
              background: linear-gradient(90deg, #1976d2 0%, #42a5f5 50%, #1976d2 100%);
              background-size: 200% 100%;
              border-radius: 4px;
              transition: width 0.5s ease-out;
            }
            
            .progress-bar-shimmer {
              position: absolute;
              top: 0;
              left: 0;
              width: 100%;
              height: 100%;
              background: linear-gradient(90deg, 
                transparent 0%, 
                transparent 20%, 
                rgba(255,255,255,0.4) 50%, 
                transparent 80%, 
                transparent 100%
              );
              animation: shimmer 2s infinite;
              pointer-events: none;
            }
          `}
        </style>
        <DialogTitle sx={{ fontWeight: 'bold', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box display="flex" alignItems="center" gap={1}>
            <AutoAwesomeIcon sx={{ color: '#1976d2', fontSize: 24 }} />
            <Typography variant="h6" sx={{ color: '#2c3e50', fontWeight: 600 }}>
              Generador de Emails
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={0.5}>
            <Tooltip title="Minimizar" PopperProps={{ style: { zIndex: 9999 } }}>
              <IconButton onClick={handleMinimize} size="small">
                <MinimizeIcon />
              </IconButton>
            </Tooltip>
            <IconButton onClick={handleCancel}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ 
          minWidth: '500px', 
          maxHeight: '80vh', 
          p: 0, // Remover padding por defecto
          display: 'flex',
          flexDirection: 'column'
        }}>
          {/* Barra de progreso sticky */}
          {isLoading && progressMessage && (
            <Box sx={{ 
              position: 'sticky',
              top: 0,
              zIndex: 10,
              bgcolor: 'white',
              borderBottom: '1px solid #e0e0e0',
              p: 2,
              mb: 0
            }}>
              <Box sx={{ 
                p: 2, 
                bgcolor: 'grey.50', 
                borderRadius: 1,
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <EmailProgressSSE progressMessage={progressMessage} />
                  {maxProgressPercentage > 0 && (
                    <Typography variant="caption" color="text.secondary">
                      {maxProgressPercentage}%
                    </Typography>
                  )}
                </Box>
                <div className="progress-bar-container">
                  <div className="progress-bar-fill" style={{ width: `${maxProgressPercentage}%` }}></div>
                  <div className="progress-bar-shimmer"></div>
                </div>
                {maxProgressPhase && maxProgressPhase !== progressMessage && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    Fase: {maxProgressPhase} {maxProgressElapsedTime && `(${maxProgressElapsedTime})`}
                  </Typography>
                )}
              </Box>
            </Box>
          )}
          
          {/* Contenido scrolleable */}
          <Box sx={{ 
            flex: 1,
            overflow: 'auto',
            p: 2
          }}>
          
          {selectedMessage && (
            <Box sx={{ 
              bgcolor: 'white', 
              p: 3, 
              borderRadius: 2, 
              mb: 2, 
              position: 'relative',
              border: '1px solid #e3f2fd',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              transition: 'all 0.2s ease',
              '&:hover': {
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                transform: 'translateY(-1px)'
              }
            }}>
              {/* Sección de configuración del hilo - mejorada */}
              <Box sx={{ 
                position: 'absolute', 
                top: 8, 
                right: 8, 
                zIndex: 2, 
                display: 'flex', 
                alignItems: 'center', 
                gap: 0.5,
                p: 0.5,
                bgcolor: '#f8f9fa',
                borderRadius: 1,
                border: '1px solid #e0e0e0'
              }}>
                <FormControlLabel
                  control={
                    <Checkbox 
                      checked={incluirHilo} 
                      onChange={e => setIncluirHilo(e.target.checked)} 
                      size="small"
                      sx={{
                        color: '#1976d2',
                        '&.Mui-checked': {
                          color: '#1976d2',
                        }
                      }}
                    />
                  }
                                     label={
                     <Typography variant="body2" sx={{ 
                       fontWeight: 500, 
                       color: '#1976d2',
                       fontSize: '0.8rem'
                     }}>
                       Incluir Hilo
                     </Typography>
                   }
                  sx={{ m: 0 }}
                />
                                 <Tooltip 
                   title={
                     <Box sx={{ p: 1 }}>
                       <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                         Configuración del Historial de Conversación
                       </Typography>
                       <Typography variant="body2" sx={{ mb: 1 }}>
                         <strong>✅ Activado:</strong> El email generado se agregará al inicio del historial, manteniendo toda la conversación anterior visible.
                       </Typography>
                       <Typography variant="body2">
                         <strong>❌ Desactivado:</strong> El email generado reemplazará completamente el contenido, eliminando el historial anterior.
                       </Typography>
                     </Box>
                   } 
                   PopperProps={{ style: { zIndex: 9999 } }}
                   arrow
                 >
                   <IconButton size="small" sx={{ p: 0.25 }}>
                     <HelpIcon fontSize="small" sx={{ color: '#1976d2' }} />
                   </IconButton>
                 </Tooltip>
              </Box>
              <Typography variant="subtitle2" sx={{ 
                fontWeight: 'bold', 
                mb: 2, 
                display: 'flex', 
                alignItems: 'center',
                color: '#2c3e50',
                fontSize: '1.1rem'
              }}>
                <InfoIcon fontSize="small" sx={{ mr: 1, color: '#2c3e50', mt: '-2px' }} />
                Respondiendo al Mensaje:
              </Typography>
              <Typography variant="body2" noWrap><strong>De:</strong> {selectedMessage.from.name} &lt;{selectedMessage.from.email}&gt;</Typography>
              <Typography variant="body2" noWrap><strong>Para:</strong> {selectedMessage.to.map(t => t.email).join(', ')}</Typography>
              <Typography variant="body2" noWrap><strong>Asunto:</strong> {selectedMessage.subject}</Typography>
              <Typography variant="body2" noWrap><strong>Fecha:</strong> {new Date(selectedMessage.receivedDateTime).toLocaleString('es-ES')}</Typography>
              <Box sx={{ 
                mt: 2, 
                pt: 2, 
                borderTop: '1px solid #e0e0e0',
                bgcolor: '#f8f9fa',
                p: 1.5,
                borderRadius: 1
              }}>
                <Typography variant="caption" color="text.secondary" sx={{ 
                  display: 'block', 
                  mb: 1,
                  fontWeight: 600,
                  color: '#666'
                }}>
                  📄 Vista previa del mensaje:
                </Typography>
                <Typography variant="body2" sx={{ 
                  color: 'text.secondary', 
                  fontSize: '0.875rem',
                  maxHeight: '60px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: 'vertical'
                }}>
                  {selectedMessage.body.replace(/\n/g, ' ').substring(0, 150)}...
                </Typography>
              </Box>
            </Box>
          )}

          {/* Información sobre la IA inteligente */}
          <Alert severity="info" sx={{ mb: 2 }} icon={<AutoAwesomeIcon />}>
            <Typography variant="body2">
              <strong>IA Inteligente:</strong> La IA analizará automáticamente el contexto y decidirá si modificar un plan existente o crear uno nuevo, basándose en el tiempo transcurrido, referencias del paciente y otros factores.
              <Tooltip
                title={<>
                  <strong>La IA modificará automáticamente un plan si:</strong>
                  <br />• El paciente respondió en los primeros 4 días del plan anterior
                  <br />• El paciente menciona "el plan", "la dieta", etc.
                  <br />• Hay desvíos reportados en contexto pronto
                  <br />• Es el final del ciclo del plan anterior
                  <br />
                  <br /><strong>La IA creará un plan nuevo si:</strong>
                  <br />• Han pasado más de 4 días del plan anterior
                  <br />• No hay referencias claras al plan anterior
                  <br />• Es el final del ciclo del plan anterior
                  <br />
                  <br /><strong>Nueva Arquitectura de 2 Llamadas:</strong>
                  <br />• Llamada 1: Análisis de contexto y generación del cuerpo del mensaje
                  <br />• Llamada 2: Generación del plan alimentario y auto-verificación
                </>}
                placement="top"
                arrow
                PopperProps={{ style: { zIndex: 9999 } }}
              >
                <IconButton size="small" sx={{ color: 'info.main', ml: 1, verticalAlign: 'middle', p: 0 }}>
                  <HelpIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Typography>
          </Alert>

          {/* Sección de Configuración Avanzada */}
          <Box sx={{ 
            mt: 3, 
            p: 3, 
            bgcolor: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
            borderRadius: 3,
            border: '1px solid #e0e0e0',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}>
            <Typography variant="h6" sx={{ 
              mb: 2, 
              color: '#2c3e50', 
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}>
              <SettingsIcon sx={{ color: '#3498db', mt: '-2px' }} />
              Configuración de Generación
            </Typography>

            <Grid container spacing={3}>
              {/* Primera fila - Configuración del Plan */}
              <Grid item xs={12}>
                <Box sx={{ 
                  p: 3, 
                  bgcolor: 'white', 
                  borderRadius: 2, 
                  border: '1px solid #e8f5e8',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    transform: 'translateY(-1px)'
                  }
                }}>
                  <Typography variant="subtitle2" sx={{ 
                    mb: 2, 
                    color: '#1b5e20', 
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5
                  }}>
                    <RestaurantMenuIcon fontSize="small" sx={{ mt: '-2px' }} />
                    Configuración del Plan Alimentario
                  </Typography>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <FormControlLabel 
                      control={
                        <Checkbox 
                          checked={options.incluirPlan} 
                          onChange={(e) => setOptions(prev => ({...prev, incluirPlan: e.target.checked}))} 
                          size="small"
                          sx={{
                            color: '#2e7d32',
                            '&.Mui-checked': {
                              color: '#2e7d32',
                            }
                          }}
                        />
                      } 
                      label={
                        <Typography variant="body2" sx={{ fontWeight: 500, color: '#2e7d32' }}>
                          Incluir Plan Alimentario
                        </Typography>
                      }
                    />
                  </Box>

                  {options.incluirPlan && (
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 2, 
                      p: 2, 
                      bgcolor: '#f1f8e9', 
                      borderRadius: 2,
                      border: '1px solid #c8e6c9'
                    }}>
                      <Tooltip title="Cantidad de días para el plan alimentario" PopperProps={{ style: { zIndex: 9999 } }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CalendarTodayIcon sx={{ color: '#2e7d32', fontSize: 20 }} />
                          <TextField
                            type="number" 
                            size="small" 
                            label="Días" 
                            value={options.numDiasObjetivo} 
                            onChange={(e) => setOptions(prev => ({...prev, numDiasObjetivo: parseInt(e.target.value)}))} 
                            sx={{ 
                              width: '100px',
                              '& .MuiOutlinedInput-root': {
                                backgroundColor: 'white',
                                '&:hover': {
                                  backgroundColor: '#f8f9fa'
                                }
                              }
                            }}
                          />
                        </Box>
                      </Tooltip>
                      
                      <Tooltip title="Día de la semana para comenzar el plan" placement="top" PopperProps={{ style: { zIndex: 9999 } }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                          <EventIcon sx={{ color: '#2e7d32', fontSize: 20 }} />
                          <Select
                            size="small" 
                            value={options.diaInicio} 
                            onChange={(e) => setOptions(prev => ({...prev, diaInicio: e.target.value}))} 
                            sx={{ 
                              flex: 1,
                              '& .MuiOutlinedInput-root': {
                                backgroundColor: 'white',
                                '&:hover': {
                                  backgroundColor: '#f8f9fa'
                                }
                              },
                              '& .MuiSelect-select': {
                                backgroundColor: 'white'
                              }
                            }}
                            MenuProps={{
                              sx: { zIndex: 2200 }
                            }}
                          >
                            {diasSemana.map(d => <MenuItem key={d} value={d}>{d}</MenuItem>)}
                          </Select>
                        </Box>
                      </Tooltip>
                    </Box>
                  )}
                </Box>
              </Grid>

              {/* Segunda fila - Instrucciones Adicionales */}
              <Grid item xs={12}>
                <Box sx={{ 
                  p: 2, 
                  bgcolor: 'white', 
                  borderRadius: 2, 
                  border: '1px solid #fff3e0',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    transform: 'translateY(-1px)'
                  }
                }}>
                  <Typography variant="subtitle2" sx={{ 
                    mb: 1, 
                    color: '#f57c00', 
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5
                  }}>
                    <EditIcon fontSize="small" />
                    Instrucciones Específicas
                  </Typography>
                  <Tooltip title="Instrucciones específicas para la IA. Tiene máxima prioridad sobre otras opciones." PopperProps={{ style: { zIndex: 9999 } }}>
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      label="Instrucción adicional (opcional)"
                      value={options.comentarioManual}
                      onChange={(e) => setOptions(prev => ({...prev, comentarioManual: e.target.value}))}
                      size="small"
                      placeholder="Ej: quiero un plan completamente nuevo, enfócate en proteínas, etc."
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: '#fffbfe',
                          '&:hover': {
                            backgroundColor: '#fff3e0'
                          }
                        }
                      }}
                    />
                  </Tooltip>
                </Box>
              </Grid>

              {/* Tercera fila - Modelos de IA (izquierda) y Tono + Opciones Avanzadas (derecha) */}
              <Grid item xs={12} md={6}>
                <Box sx={{ 
                  p: 2, 
                  bgcolor: 'white', 
                  borderRadius: 2, 
                  border: '1px solid #e1f5fe',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    transform: 'translateY(-1px)'
                  }
                }}>
                  <Typography variant="subtitle2" sx={{ 
                    mb: 1, 
                    color: '#0277bd', 
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5
                  }}>
                    <SmartToyIcon fontSize="small" />
                    Modelos de IA
                  </Typography>
                  
                  {/* Modelo Principal (Llamada 1) */}
                  <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: '#01579b' }}>
                    Análisis y Cuerpo del Email:
                  </Typography>
                  <Select 
                    size="small" 
                    fullWidth 
                    value={options.model} 
                    onChange={(e) => setOptions(prev => ({...prev, model: e.target.value}))}
                    MenuProps={{ sx: { zIndex: 2200 } }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#f0f8ff',
                        '&:hover': { backgroundColor: '#e1f5fe' }
                      }
                    }}
                  >
                    {availableModels.map(m => <MenuItem key={m} value={m}>{m.split('/')[1] || m}</MenuItem>)}
                  </Select>
                  
                  {/* Modelo Secundario (Llamada 2) */}
                  <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, mt: 2, color: '#01579b' }}>
                    Plan Alimentario y Verificación:
                  </Typography>
                  <Select 
                    size="small" 
                    fullWidth 
                    value={options.modelCall2 || ''} 
                    onChange={(e) => setOptions(prev => ({...prev, modelCall2: e.target.value}))}
                    MenuProps={{ sx: { zIndex: 2200 } }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#f8f9fa',
                        '&:hover': { backgroundColor: '#e9ecef' }
                      }
                    }}
                  >
                    {availableModels.map(m => <MenuItem key={m} value={m}>{m.split('/')[1] || m}</MenuItem>)}
                  </Select>

                  {/* Modelo economico para jueces del cuerpo */}
                  <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, mt: 2, color: '#01579b' }}>
                    Jueces / bajo costo:
                  </Typography>
                  <Select
                    size="small"
                    fullWidth
                    value={options.modelJudge || ''}
                    onChange={(e) => setOptions(prev => ({...prev, modelJudge: e.target.value}))}
                    MenuProps={{ sx: { zIndex: 2200 } }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#fffdf5',
                        '&:hover': { backgroundColor: '#fff8e1' }
                      }
                    }}
                  >
                    {availableModels.map(m => <MenuItem key={m} value={m}>{m.split('/')[1] || m}</MenuItem>)}
                  </Select>
                </Box>
              </Grid>

              {/* Columna derecha - Tono de Comunicación y Opciones Avanzadas */}
              <Grid item xs={12} md={6}>
                {/* Tono de Comunicación */}
                <Box sx={{ 
                  p: 2, 
                  bgcolor: 'white', 
                  borderRadius: 2, 
                  border: '1px solid #e3f2fd',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  transition: 'all 0.2s ease',
                  mb: 2,
                  '&:hover': {
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    transform: 'translateY(-1px)'
                  }
                }}>
                  <Typography variant="subtitle2" sx={{ 
                    mb: 1, 
                    color: '#1976d2', 
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5
                  }}>
                    <RecordVoiceOverIcon fontSize="small" />
                    Tono de Comunicación
                  </Typography>
                  <Select 
                    size="small" 
                    fullWidth 
                    value={options.toneStyle} 
                    onChange={(e) => setOptions(prev => ({...prev, toneStyle: e.target.value as any}))}
                    MenuProps={{
                      sx: { zIndex: 2200 }
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#f8f9fa',
                        '&:hover': {
                          backgroundColor: '#e3f2fd'
                        }
                      }
                    }}
                  >
                    {tonos.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                  </Select>
                </Box>

                {/* Opciones Avanzadas */}
                <Box sx={{ 
                  p: 2, 
                  bgcolor: 'white', 
                  borderRadius: 2, 
                  border: '1px solid #fce4ec',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    transform: 'translateY(-1px)'
                  }
                }}>
                  <Typography variant="subtitle2" sx={{ 
                    mb: 1, 
                    color: '#880e4f', 
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5
                  }}>
                    <PriorityHighIcon fontSize="small" sx={{ mt: '-2px' }} />
                    Opciones Avanzadas
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={options.editarPlanActivo}
                          onChange={(e) => setOptions(prev => ({ ...prev, editarPlanActivo: e.target.checked }))}
                          size="small"
                          sx={{
                            color: '#ad1457',
                            '&.Mui-checked': {
                              color: '#ad1457',
                            }
                          }}
                        />
                      }
                      label={
                        <Typography variant="body2" sx={{ fontWeight: 500, color: '#ad1457' }}>
                          Forzar Edición de Plan
                        </Typography>
                      }
                    />
                    <Tooltip title="Fuerza a la IA a modificar el plan más reciente en lugar de decidir automáticamente." PopperProps={{ style: { zIndex: 9999 } }}>
                      <IconButton size="small" sx={{ p: 0.5 }}>
                        <HelpIcon fontSize="small" sx={{ color: '#ad1457' }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              </Grid>
              

            </Grid>
          </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: '16px 24px' }}>
          {isLoading ? (
            <Button onClick={handleCancel} disabled={false}>Cancelar</Button>
          ) : (
            <Button onClick={onClose} disabled={isLoading || hasBlockingReviewPending}>Cancelar</Button>
          )}
          <Button 
            onClick={handleInternalGenerate}
            variant="contained" 
            disabled={isLoading || hasBlockingReviewPending}
            startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {isLoading ? 'Generando...' : 'Generar'}
          </Button>
        </DialogActions>

        {/* Modal de confirmación de peso */}
        <Dialog 
          open={!!modalPeso}
          onClose={(event, reason) => {
            if (reason !== 'escapeKeyDown') return;
            setModalPeso(null);
          }}
          sx={{ zIndex: 2100 }}
          PaperProps={{
            sx: {
              mt: 1,
              borderRadius: 3,
              boxShadow: 8,
              minWidth: 340,
              maxWidth: 400,
              margin: '0 auto',
              transform: 'translateY(-80px)', // 80 píxeles más arriba
            }
          }}
        >
          <DialogTitle sx={{ fontWeight: 'bold', pb: 0, pt: 2, fontSize: 20, textAlign: 'center' }}>
            <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
              <span role="img" aria-label="balanza">⚖️</span>
              Confirmar nuevo peso
            </Box>
          </DialogTitle>
          <DialogContent sx={{ pt: 1, pb: 0 }}>
            <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main', mb: 1 }}>
                {modalPeso?.peso !== undefined ? Number(modalPeso.peso).toFixed(3) : ''} kg
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Fecha: <b>{modalPeso?.fecha}</b>
              </Typography>
              <Typography variant="body1" sx={{ textAlign: 'center', mb: 1 }}>
                {modalPeso?.mensaje}
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
            <Button onClick={() => handleConfirmarPeso(false)} color="inherit" variant="outlined">No guardar</Button>
            <Button onClick={() => handleConfirmarPeso(true)} variant="contained" color="primary">Guardar</Button>
          </DialogActions>
        </Dialog>
      </Dialog>
      
      {/* Modal de confirmación de cancelación */}
      <Dialog 
        open={showCancelConfirmation}
        onClose={handleCancelConfirmation}
        sx={{ zIndex: 2100 }}
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: 8,
            minWidth: 340,
            maxWidth: 400,
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 'bold', pb: 1, pt: 2, fontSize: 20, textAlign: 'center' }}>
          Cancelar generación
        </DialogTitle>
        <DialogContent sx={{ pt: 1, pb: 0 }}>
          <Typography variant="body1" sx={{ textAlign: 'center', mb: 1 }}>
            ¿Quieres cancelar la generación?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 2, gap: 2 }}>
          <Button onClick={handleCancelConfirmation} variant="outlined" color="primary">
            Continuar
          </Button>
          <Button onClick={handleConfirmCancel} variant="contained" color="error">
            Cancelar
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Snackbar y backdrop globales, fuera del Dialog */}
      {showSuccessModal && ReactDOM.createPortal(
        <>
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100vw',
              height: '100vh',
              bgcolor: 'rgba(0,0,0,0.5)',
              zIndex: 3000,
            }}
          />
          <Snackbar 
            open={showSuccessModal} 
            autoHideDuration={2500} 
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            sx={{
              top: '20% !important',
              transform: 'translateY(0)',
              zIndex: 3100,
            }}
            onClose={handleSuccessSnackbarClose}
          >
            <Alert severity="success" sx={{ width: '100%' }}>
              Generación completada
            </Alert>
          </Snackbar>
        </>,
        document.body
      )}

      {/* Modal de Recordatorios */}
      <RecordatoriosModal
        isOpen={showRecordatoriosModal}
        onClose={handleCloseRecordatoriosModal}
        recordatorios={recordatoriosDetectados}
        pacienteId={pacienteData?.id || ''}
        pacienteNombre={pacienteData?.nombre || 'Paciente'}
        onRecordatoriosGuardados={handleRecordatoriosGuardados}
      />
    </>
  );
};

export default AIEmailGenerator; 
